import React from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {CertificationPracticeExamCarousel} from '@udemy/react-certification-preparation';
import {
    CourseUnit,
    LabUnit,
    PopularTopicsUnit,
    SingleCourseUnit,
    SkillsHubUnit,
} from '@udemy/react-discovery-units';
import {PopularInstructorsUnit} from '@udemy/react-discovery-units';
import {AssessmentUnit} from '@udemy/react-pro-content';
import {udSentry} from '@udemy/sentry';

import {withAboveTheFoldProvider} from '../../../components/above-the-fold/with-above-the-fold-provider.react-component';
import {TopicPpMerchandisingModule} from './topic-pp-merchandising-module/topic-pp-merchandising-module.react-component';

export const SingleCourseUnitAboveTheFold = withAboveTheFoldProvider(SingleCourseUnit);
export const SkillsHubUnitAboveTheFold = withAboveTheFoldProvider(SkillsHubUnit);

/**
 * SkillsHub wrapped with product-card context so cards render through
 * BrowseCourseProductCard (supports layout="vertical"). Used on mobile
 * for the shipped mx_mobile_vertical_reco_units variant.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SkillsHubUnitWithProductCards: React.FC<any> = (props) => (
    <AsyncPriceCourseCardContext.Provider
        value={{useCourseProductCard: true, hideCourseDescription: true}}
    >
        <SkillsHubUnitAboveTheFold {...props} />
    </AsyncPriceCourseCardContext.Provider>
);

/**
 * Below units are units that are available to be rendered in any Topic Page. Including MX, Subs, UB, Free etc.
 * Note that, these are the units that are coming from the Discovery API context
 * @remarks
 *
 * IF YOU ARE ADDING A NEW UNIT, PLEASE MAKE SURE TO ADD IT TO THE BELOW LIST AND MAKE SURE THAT THE UNIT IS COMING
 * FROM THE DISCOVERY API CONTEXT AS WELL. OTHERWISE, IT WILL NOT BE RENDERED.
 */
export const AVAILABLE_UNIT_TYPES = {
    ALGORITHM_BASED_ASSESSMENTS_WITHOUT_FILTER: 'algorithm_based_assessments_without_filter',
    ALGORITHM_BASED_WITH_FILTERS: 'algorithm_based_with_filters',
    BESTSELLER_LABELS: 'bestseller_labels',
    ALGORITHM_BASED_LABS_WITHOUT_FILTER: 'algorithm_based_labs_without_filter',
    FEATURED_COURSE: 'featured_course',
    TOP_INSTRUCTOR: 'top_instructor',
    BESTSELLER: 'bestseller',
    BESTSELLER_PRACTICE_TEST_COURSE: 'bestseller_practice_test_course',
    PERSONAL_PLAN_MERCHANDISING: 'personal_plan_merchandising',
};

/**
 * Below view types are view types that are available to be rendered in any Topic Page.
 * For some components, we're prioritizing view types over unit types such as Skills Hub Unit.
 *
 * IF YOU ARE ADDING A NEW UNIT, PLEASE MAKE SURE TO ADD IT TO THE BELOW LIST.
 */
export const AVAILABLE_UNIT_VIEW_TYPES = {
    HORIZONTAL_TABBED: 'horizontal_tabbed',
    /** Recommendations “topic subscription upsell” bestseller surface (same GraphQL context as Skills Hub). */
    TOPIC_SUBSCRIPTION_UPSELL: 'topic_subscription_upsell',
    DEFAULT: 'default',
    CERTIFICATION_PRACTICE_EXAM_CAROUSEL: 'certification_practice_exam_carousel',
};

/** Practice-exam carousel unit from Discovery (`bestseller_practice_test_course` or matching view type). */
export function isPracticeExamDiscoveryUnit(unit: Partial<DiscoveryUnit>): boolean {
    const viewType = unit.view_type ?? (unit as {viewType?: string}).viewType;
    return (
        unit.type === AVAILABLE_UNIT_TYPES.BESTSELLER_PRACTICE_TEST_COURSE ||
        viewType === AVAILABLE_UNIT_VIEW_TYPES.CERTIFICATION_PRACTICE_EXAM_CAROUSEL
    );
}

/** Backend `bestseller` unit with `view_type` `topic_subscription_upsell` (Personal Plan merchandising on topic). */
export function isTopicSubscriptionUpsellBestsellerUnit(unit: Partial<DiscoveryUnit>): boolean {
    const viewType =
        unit.view_type ??
        (unit as {viewType?: string}).viewType ??
        AVAILABLE_UNIT_VIEW_TYPES.DEFAULT;
    return (
        unit.type === AVAILABLE_UNIT_TYPES.BESTSELLER &&
        viewType === AVAILABLE_UNIT_VIEW_TYPES.TOPIC_SUBSCRIPTION_UPSELL
    );
}

export type AvailableDiscoveryUnitType =
    (typeof AVAILABLE_UNIT_TYPES)[keyof typeof AVAILABLE_UNIT_TYPES];
export type AvailableDiscoveryUnitViewType =
    (typeof AVAILABLE_UNIT_VIEW_TYPES)[keyof typeof AVAILABLE_UNIT_VIEW_TYPES];

/**
 * Below mappings are currently being rendered on any Topic Page.
 *
 * Previously, we were relying on DiscoveryUnitsContainer to pick correct container but we've removed that logic.
 * Now consumers provide the correct component to be rendered to DiscoveryUnitsContainer.
 *
 * Currently, we have two level unit to component mapping:
 *  - Unit Type to Component (L1)
 *  - Unit Type
 *       |___ Unit View Type to Component (L2)
 *
 * @remarks
 * IF YOU ARE ADDING A NEW UNIT, PLEASE MAKE SURE TO ADD IT TO THE BELOW MAPPINGS.
 */
export const getTopicDiscoveryUnitComponentByUnitTypes = (
    unitType: AvailableDiscoveryUnitType,
    unitViewType?: AvailableDiscoveryUnitViewType,
) => {
    switch (unitType) {
        case AVAILABLE_UNIT_TYPES.BESTSELLER_LABELS:
            return PopularTopicsUnit;
        case AVAILABLE_UNIT_TYPES.ALGORITHM_BASED_LABS_WITHOUT_FILTER:
            return LabUnit;
        case AVAILABLE_UNIT_TYPES.ALGORITHM_BASED_ASSESSMENTS_WITHOUT_FILTER:
            return AssessmentUnit;
        case AVAILABLE_UNIT_TYPES.FEATURED_COURSE:
            return SingleCourseUnitAboveTheFold;
        case AVAILABLE_UNIT_TYPES.TOP_INSTRUCTOR:
            return PopularInstructorsUnit;
        case AVAILABLE_UNIT_TYPES.PERSONAL_PLAN_MERCHANDISING:
            return TopicPpMerchandisingModule;
        case AVAILABLE_UNIT_TYPES.ALGORITHM_BASED_WITH_FILTERS:
            return CourseUnit;
        case AVAILABLE_UNIT_TYPES.BESTSELLER_PRACTICE_TEST_COURSE:
            return CertificationPracticeExamCarousel;
        case AVAILABLE_UNIT_TYPES.BESTSELLER:
            switch (unitViewType) {
                case AVAILABLE_UNIT_VIEW_TYPES.HORIZONTAL_TABBED:
                    return SkillsHubUnitAboveTheFold;
                case AVAILABLE_UNIT_VIEW_TYPES.TOPIC_SUBSCRIPTION_UPSELL:
                    return TopicPpMerchandisingModule;
                case AVAILABLE_UNIT_VIEW_TYPES.DEFAULT:
                    return CourseUnit;
                case AVAILABLE_UNIT_VIEW_TYPES.CERTIFICATION_PRACTICE_EXAM_CAROUSEL:
                    return CertificationPracticeExamCarousel;
                default:
                    // GraphQL / Tapen may omit view_type; category falls back to CourseUnit for unknown bestseller views
                    return CourseUnit;
            }
    }
};

/**
 * Get available discovery unit components and props for DiscoveryUnitContainer
 * @param unit - Discovery Unit to be mapped to a component
 * @param unitsProps - Props for the unit components to be passed. Otherwise it will be an empty object
 */
export const getAvailableDiscoveryUnitComponentsAndProps = (
    unit: DiscoveryUnit,
    unitsProps?: Record<
        AvailableDiscoveryUnitType | AvailableDiscoveryUnitViewType,
        Record<string, unknown>
    >,
) => {
    const viewType =
        unit.view_type ??
        (unit as {viewType?: string}).viewType ??
        AVAILABLE_UNIT_VIEW_TYPES.DEFAULT;

    const injectDefaultComponentProps = (props: Record<string, unknown> = {}) => {
        return {
            ...props,
            /**
             * This is a necessary addition to make sure that the components are rendered with the correct margin.
             * Current discovery units container component margin is working if current component has above sibling
             * that has again, component-margin class.
             *
             * Since we're injecting components from outside to discovery units container, className are not being matched.
             * For example like:
             *
             * <div class="component-margin"/> ----------------------------------> Injected by us
             * <div class="discovery-units-container_component-margin-XYZY"/> ---> No margin, but it should have
             * <div class="component-margin"/> ----------------------------------> Injected by us
             * <div class="discovery-units-container_component-margin-XYZY"/> ---> No margin, but it should have
             *
             * So rather than using mixed class names and seeing this problems, for now, we're overriding the className
             * of all of the units that we're passing to discovery units container.
             *
             * TODO: Please fix this on the Discovery Units Container side. Consumer either rely on it or they should
             * provide margins all to get all the time.
             */
            className: props?.className ?? 'component-margin',
        };
    };

    const componentFromUnitTypeAndView = getTopicDiscoveryUnitComponentByUnitTypes(
        unit.type,
        viewType as AvailableDiscoveryUnitViewType,
    );

    if (componentFromUnitTypeAndView) {
        const componentProps = injectDefaultComponentProps(
            unitsProps?.[unit.type] ?? unitsProps?.[viewType],
        );
        return {
            component: componentFromUnitTypeAndView,
            componentProps: componentProps,
        };
    }

    udSentry.captureException(
        new Error(
            `Missing component for unit type: ${unit.type} & view type: ${viewType} for topic page}`,
        ),
    );

    /**
     * This is mainly for unblocking the rendering of the page. Currently, discovery units is just throwing an
     * error and this is the only way to prevent it, easily at least.
     *
     * TODO: Fix this on the Discovery Units Container side. And make above configurable for consumers
     */
    return {
        component: () => null,
        componentProps: {},
    };
};
